import React, { useEffect, useMemo, useRef, useState } from "react";
import { QokrAvatar } from "../QokrPrimitives/QokrPrimitives";
import "../QokrPrimitives/_qokr-avatar.scss";
import "./_qokr-person-select.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The scorecard's Individual OKRs person picker — one dropdown in place of a
// row of filter pills. "All team members" by default; each person shows how
// many objectives they hold. A search box appears once the team is long.
//
// `people`: [{ user_id, name, is_deactivated?, count }] · `value`: the picked
// user id as a string, or null for everyone · `onChange(id | null)`.
// Shared, byte-identical, by the CEO app and the coach app.
// ═══════════════════════════════════════════════════════════════════════════

const SEARCH_FROM = 7;

const countLabel = (n) => (n === 1 ? "1 objective" : `${n} objectives`);

const AllIcon = () => (
  <span className="qokr-psel__all-icon" aria-hidden="true">
    <i className="fa-solid fa-users" />
  </span>
);

const QokrPersonSelect = ({ people = [], value = null, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const selected =
    value == null
      ? null
      : people.find((p) => String(p.user_id) === String(value)) || null;
  const total = people.reduce((sum, p) => sum + (Number(p.count) || 0), 0);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? people.filter((p) => (p.name || "").toLowerCase().includes(q))
      : people;
  }, [people, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    if (people.length >= SEARCH_FROM) searchRef.current?.focus();
    const onDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, people.length]);

  const pick = (id) => {
    setOpen(false);
    onChange?.(id == null ? null : String(id));
  };

  const option = (id, label, sub, icon) => {
    const isSelected =
      id == null ? value == null : String(value) === String(id);
    return (
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        key={id == null ? "all" : id}
        className={`qokr-psel__option${isSelected ? " is-selected" : ""}`}
        onClick={() => pick(id)}
      >
        {icon}
        <span className="qokr-psel__option-text">
          <span className="qokr-psel__option-name">{label}</span>
          <span className="qokr-psel__option-sub">{sub}</span>
        </span>
        {isSelected ? (
          <i
            className="fa-solid fa-check qokr-psel__check"
            aria-hidden="true"
          />
        ) : null}
      </button>
    );
  };

  return (
    <div className="qokr-psel" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`qokr-psel__trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {selected ? <QokrAvatar person={selected} size="sm" /> : <AllIcon />}
        <span className="qokr-psel__text">
          <span className="qokr-psel__eyebrow">Team member</span>
          <span className="qokr-psel__value">
            {selected ? selected.name : "All team members"}
          </span>
        </span>
        <i
          className={`fa-solid fa-chevron-down qokr-psel__chev${open ? " is-open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div className="qokr-psel__menu">
          {people.length >= SEARCH_FROM ? (
            <div className="qokr-psel__search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder="Search team members"
                aria-label="Search team members"
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          ) : null}
          <div
            className="qokr-psel__list"
            role="listbox"
            aria-label="Team members"
          >
            {!query.trim()
              ? option(null, "All team members", countLabel(total), <AllIcon />)
              : null}
            {shown.length && !query.trim() ? (
              <div className="qokr-psel__divider" />
            ) : null}
            {shown.map((person) =>
              option(
                person.user_id,
                person.is_deactivated
                  ? `${person.name} (deactivated)`
                  : person.name,
                countLabel(Number(person.count) || 0),
                <QokrAvatar person={person} size="sm" />,
              ),
            )}
            {!shown.length ? (
              <div className="qokr-psel__empty">No one matches.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QokrPersonSelect;
